<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { computed, inject, nextTick, onMounted, ref, watch } from "vue";
import { vDragScroll } from "#/directives/vDragScroll";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import type { IGroupModifiers, ISelectModifiers } from "#/types/iCardProduct";
import { formatPrice, getLocaleName } from "#/utils/i18next";
import { liquidModifiersKey } from "./liquidModifiersContext";

const MAX_PREVIEWS = 3;

const props = defineProps<{ groups: IGroupModifiers[]; activeGroupId: string | null }>();

const emit = defineEmits<(e: "select", group: IGroupModifiers) => void>();

const { t, i18next } = useTranslation();

const themeStore = useThemeStore();
const mainStore = useMainStore();

const ctx = inject(liquidModifiersKey);
if (!ctx) throw new Error("LiquidModifiersBar: missing liquidModifiersKey provider");

const barRef = ref<HTMLElement | null>(null);

const getImage = (item: { image_link?: string | null } | null | undefined) => item?.image_link || themeStore.data?.fallback_image || "";

const formatModifierPrice = (price: number) => {
	const text = formatPrice(Math.max(price, 0), mainStore.config_lib?.currency_symbol);
	return price > 0 ? `+${text}` : text;
};

const getPriceRange = (modifiers: ISelectModifiers[]) => {
	if (!modifiers.length) return "";
	const prices = modifiers.map((modifier) => Math.max(ctx.getModifierPrice(modifier), 0));
	const min = Math.min(...prices);
	const text = formatPrice(min, mainStore.config_lib?.currency_symbol);
	return min === Math.max(...prices) ? text : `${t(`card.price.from`)} ${text}`;
};

const items = computed(() =>
	props.groups.map((group) => {
		const visible = group.group_modifiers.filter((modifier) => !ctx.isHiddenModifier(modifier, group));
		const selected = visible.filter((modifier) => ctx.getAmount(modifier, group) > 0);
		const count = selected.reduce((sum, modifier) => sum + ctx.getAmount(modifier, group), 0);
		const previews = selected.flatMap((modifier) => Array.from({ length: ctx.getAmount(modifier, group) }, () => getImage(modifier)));
		const single = count === 1 ? selected[0] : null;

		let caption = getPriceRange(visible);
		if (single) caption = formatModifierPrice(ctx.getModifierPrice(single));
		else if (count > 1) caption = t(`card.modifiers.selected`, { selected: count });

		return {
			group,
			name: getLocaleName(group, i18next.language),
			caption,
			image: getImage(group.image_link ? group : visible[0]),
			previews: previews.slice(0, MAX_PREVIEWS),
			extra: Math.max(previews.length - MAX_PREVIEWS, 0),
			isRequired: !ctx.isGroupSatisfied(group),
		};
	}),
);

const centerActive = (behavior: ScrollBehavior) => {
	const bar = barRef.value;
	if (!bar || !props.activeGroupId) return;
	const target = bar.querySelector<HTMLElement>(`[data-liquid-bar-group="${props.activeGroupId}"]`);
	if (!target) return;
	bar.scrollTo({ left: Math.max(target.offsetLeft - (bar.clientWidth - target.offsetWidth) / 2, 0), behavior });
};

watch(
	() => props.activeGroupId,
	async () => {
		await nextTick();
		centerActive("smooth");
	},
);

onMounted(() => centerActive("auto"));
</script>

<template>
	<div ref="barRef" v-drag-scroll class="cms_bar">
		<button
			v-for="item in items"
			:key="item.group.id"
			type="button"
			class="cms_bar_item"
			:class="{ cms_bar_item_active: item.group.id === activeGroupId, cms_bar_item_required: item.isRequired }"
			:data-liquid-bar-group="item.group.id"
			@click="emit('select', item.group)"
		>
			<span v-if="item.previews.length" class="cms_bar_previews">
				<img v-for="(image, index) in item.previews" :key="index" :src="image" class="cms_bar_preview" alt="">
				<span v-if="item.extra" class="cms_bar_extra">+{{ item.extra }}</span>
			</span>
			<span class="cms_bar_image">
				<img v-if="item.image" :src="item.image" alt="">
			</span>
			<span class="cms_bar_text">
				<span class="cms_bar_name">{{ item.name }}</span>
				<span v-if="item.caption" class="cms_bar_caption">{{ item.caption }}</span>
			</span>
		</button>
	</div>
</template>

<style scoped lang="scss">
  @use './liquidModifiersBar.scss';
</style>
